import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useApiEp from "../hooks/useApiEp";
import {
  Alert,
  Autocomplete,
  Box,
  Container,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Divider,
  TextField,
  Stack,
} from "@mui/material";
import BackBtn from "../widgets/BackBtn";
import MuPb from "../widgets/MuPb";
import { getErrorResponse, getHeader } from "../utils/util";
import {
  FolderOpen,
  Calendar,
  Smartphone,
  Mic,
  Trash2,
  Zap,
  Plus,
  Users,
  Pencil,
  Eye,
  X,
  BarChart2,
  Database,
} from "lucide-react";
import { MessageCircle } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { MessagesSquare } from "lucide-react";
import axios from "axios";
import { API_ENDPOINT } from "../utils/constants";
import { RcsTemplatePreview } from "../components/rcs/RcsTemplates";
import TemplatePreview from "../widgets/TemplatePreview";
import { toast } from "react-toastify";
import CampaignReportTabs from "./CampaignReportTabs";

/* ── small stat card ─────────────────────────────────────── */
const StatCard = ({ icon: Icon, label, count, color }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2.5,
      px: 2.5,
      py: 2,
      bgcolor: "background.paper",
      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      display: "flex",
      alignItems: "center",
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: 2,
        bgcolor: `${color}.50`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon
        size={24}
        strokeWidth={1.8}
        style={{ color: color === "primary" ? "#1976d2" : "#7b1fa2" }}
      />
    </Box>
    <Box>
      <Typography variant="h6" fontWeight={700} lineHeight={1.1}>
        {count}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Box>
);

/* ── template view modal (inner) ─────────────────────────── */
const TemplateViewModalInner = ({ templateId, type, onClose, cDetail }) => {
  const isRcs = type === "rcs";

  const config = isRcs
    ? {
        endpoint: `/rcs/get-template/${templateId}`,
        Icon: Smartphone,
        iconColor: "#7b1fa2",
        bgColor: "secondary.50",
        chipLabel: "RCS",
        chipColor: "secondary",
      }
    : {
        endpoint: `/api/get-template/${templateId}`,
        Icon: MessageCircle,
        iconColor: "#1976d2",
        bgColor: "primary.50",
        chipLabel: "WhatsApp",
        chipColor: "primary",
      };

  const {
    data: template,
    isLoading,
    isError,
    error,
  } = useApiEp(config.endpoint);

  const previewUrl = useMemo(() => {
    if (isRcs || !template || !cDetail?.wabaCampaigns) return "";
    const campaign = cDetail.wabaCampaigns.find(
      (item) => item.templateId === template.id
    );
    return campaign?.header?.mediaUrl ?? "";
  }, [isRcs, template, cDetail?.wabaCampaigns]);

  const renderContent = () => {
    if (isLoading)
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            py: 4,
            width: "100%",
          }}
        >
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Loading template…
          </Typography>
        </Box>
      );

    if (isError)
      return (
        <Alert severity="error" sx={{ borderRadius: 2, width: "100%" }}>
          {getErrorResponse(error) ?? "Failed to load template."}
        </Alert>
      );

    if (!template)
      return (
        <Alert severity="warning" sx={{ borderRadius: 2, width: "100%" }}>
          No template data found.
        </Alert>
      );

    return isRcs ? (
      <RcsTemplatePreview template={template} />
    ) : (
      <TemplatePreview template={template} previewUrl={previewUrl} />
    );
  };

  return (
    <>
      <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 1 }}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            bgcolor: "grey.100",
            "&:hover": { bgcolor: "grey.200" },
            borderRadius: 1.5,
          }}
        >
          <X size={16} strokeWidth={2} />
        </IconButton>
      </Box>

      <DialogTitle
        sx={{
          fontWeight: 700,
          pr: 6,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: config.bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <config.Icon
            size={16}
            strokeWidth={1.8}
            style={{ color: config.iconColor }}
          />
        </Box>
        Template Preview
        <Chip
          label={config.chipLabel}
          size="small"
          color={config.chipColor}
          sx={{ height: 20, fontSize: 10, ml: 0.5 }}
        />
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{
          pt: 3,
          pb: 3,
          minHeight: 220,
          display: "flex",
          alignItems: isLoading || isError ? "center" : "flex-start",
          justifyContent: isLoading || isError ? "center" : "flex-start",
        }}
      >
        {renderContent()}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
          ID: {templateId}
        </Typography>
        <Button
          onClick={onClose}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          Close
        </Button>
      </DialogActions>
    </>
  );
};

/* ── template view modal (wrapper) ──────────────────────── */
const TemplateViewModal = ({ open, onClose, templateId, type, cDetail }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: { borderRadius: 3, position: "relative", overflow: "visible" },
    }}
  >
    {open && templateId && (
      <TemplateViewModalInner
        templateId={templateId}
        type={type}
        onClose={onClose}
        cDetail={cDetail}
      />
    )}
  </Dialog>
);

/* ── contact list dialog ─────────────────────────────────── */
const ContactListDialog = ({
  open,
  onClose,
  currentList,
  listData,
  listLoading,
  listError,
  isDb,
  onSubmit,
  submitting,
  submitError,
}) => {
  const [selected, setSelected] = useState(currentList ?? null);

  React.useEffect(() => {
    if (open) setSelected(currentList ?? null);
  }, [open, currentList]);

  const options = listData ?? [];
  const isChange = Boolean(currentList);

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      PaperProps={{ sx: { borderRadius: 3, p: 1, minWidth: 420 } }}
    >
      <DialogTitle
        sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}
      >
        {isChange
          ? isDb
            ? "Change Database"
            : "Change Contact List"
          : isDb
          ? "Add Database"
          : "Add Contact List"}
        {isDb && (
          <Chip
            icon={<Database size={11} strokeWidth={2} />}
            label="Database"
            size="small"
            color="warning"
            sx={{ height: 20, fontSize: 10 }}
          />
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: "8px !important" }}>
        <DialogContentText sx={{ mb: 2.5 }}>
          {isChange
            ? isDb
              ? "Select a different database to associate with this campaign group."
              : "Select a different contact list to associate with this campaign group."
            : isDb
            ? "Choose a database to associate with this campaign group."
            : "Choose a contact list to associate with this campaign group."}
        </DialogContentText>

        {listLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : listError ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {isDb
              ? "Failed to load databases."
              : "Failed to load contact lists."}
          </Alert>
        ) : (
          <Autocomplete
            options={options}
            value={selected}
            onChange={(_, val) => setSelected(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={isDb ? "Database" : "Contact List"}
                placeholder={
                  isDb
                    ? "Search and select a database…"
                    : "Search and select a contact list…"
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            )}
            noOptionsText={
              isDb ? "No databases found" : "No contact lists found"
            }
            isOptionEqualToValue={(opt, val) => opt === val}
            fullWidth
          />
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={submitting || !selected || selected === currentList}
          onClick={() => onSubmit(selected)}
          startIcon={
            submitting ? (
              <CircularProgress size={15} color="inherit" />
            ) : isDb ? (
              <Database size={15} />
            ) : (
              <Users size={15} />
            )
          }
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          {submitting
            ? "Saving…"
            : isChange
            ? isDb
              ? "Update Database"
              : "Update List"
            : isDb
            ? "Add Database"
            : "Add List"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── campaign delete confirmation dialog ─────────────────── */
const CampaignDeleteDialog = ({
  open,
  onClose,
  campaign,
  deleting,
  deleteError,
  onConfirm,
}) => {
  const isRcs = campaign?.type === "rcs";
  const Icon = isRcs ? Smartphone : MessageCircle;
  const chipColor = isRcs ? "secondary" : "primary";
  const chipLabel = isRcs ? "RCS" : "WhatsApp";

  return (
    <Dialog
      open={open}
      onClose={() => !deleting && onClose()}
      PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 420 } }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", pt: 3, pb: 1 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            bgcolor: "error.50",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Trash2 size={24} strokeWidth={1.8} style={{ color: "#d32f2f" }} />
        </Box>
      </Box>

      <DialogTitle
        sx={{
          fontWeight: 700,
          textAlign: "center",
          pt: 1,
          pb: 0.5,
          fontSize: "1.1rem",
        }}
      >
        Remove Campaign from Group?
      </DialogTitle>

      <DialogContent sx={{ textAlign: "center", pt: "8px !important" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: 1.5,
            py: 0.75,
            mb: 2,
          }}
        >
          <Icon
            size={14}
            strokeWidth={2}
            style={{ color: isRcs ? "#7b1fa2" : "#1976d2", flexShrink: 0 }}
          />
          <Typography variant="body2" fontWeight={700} noWrap>
            {campaign?.name}
          </Typography>
          <Chip
            label={chipLabel}
            size="small"
            color={chipColor}
            sx={{ height: 18, fontSize: 10 }}
          />
        </Box>

        <DialogContentText sx={{ fontSize: "0.875rem" }}>
          This campaign will be removed from the group. The campaign itself will
          not be deleted and can be re-added later.
        </DialogContentText>

        {deleteError && (
          <Alert
            severity="error"
            sx={{ mt: 2, borderRadius: 2, textAlign: "left" }}
          >
            {deleteError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, justifyContent: "center" }}>
        <Button
          onClick={onClose}
          disabled={deleting}
          variant="outlined"
          sx={{ borderRadius: 2, textTransform: "none", minWidth: 110 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={deleting}
          onClick={onConfirm}
          startIcon={
            deleting ? (
              <CircularProgress size={15} color="inherit" />
            ) : (
              <Trash2 size={15} />
            )
          }
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            minWidth: 110,
          }}
        >
          {deleting ? "Removing…" : "Remove"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── main component ──────────────────────────────────────── */
const CampaignGroupDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isError,
    error,
    mutate,
    refetch: reloadDetail,
  } = useApiEp(`/campaign/campaign-group/${id}`);

  // Determine isDb after data loads — default false
  const isDb = Boolean(data?.isDb);

  // Conditionally fetch the right list based on isDb
  const {
    data: groupListData,
    isLoading: groupListLoading,
    isError: groupListError,
  } = useApiEp(isDb ? `/user/get-my-assigned-dbs` : `/user/group-list`);

  // Delete group dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Contact list dialog state
  const [contactListOpen, setContactListOpen] = useState(false);
  const [contactListSubmitting, setContactListSubmitting] = useState(false);
  const [contactListError, setContactListError] = useState(null);

  // Template view modal state
  const [templateModal, setTemplateModal] = useState({
    open: false,
    templateId: null,
    type: null,
  });

  // Campaign delete dialog state
  const [campaignDeleteDialog, setCampaignDeleteDialog] = useState({
    open: false,
    name: null,
    type: null,
  });
  const [campaignDeleting, setCampaignDeleting] = useState(false);
  const [campaignDeleteError, setCampaignDeleteError] = useState(null);

  // Base file ID removal state
  const [removingBaseFile, setRemovingBaseFile] = useState(false);
  const [removeBaseFileError, setRemoveBaseFileError] = useState(null);

  const openTemplateModal = (templateId, type) =>
    setTemplateModal({ open: true, templateId, type });

  const closeTemplateModal = () =>
    setTemplateModal({ open: false, templateId: null, type: null });

  const openCampaignDeleteDialog = (name, type) => {
    setCampaignDeleteError(null);
    setCampaignDeleteDialog({ open: true, name, type });
  };

  const closeCampaignDeleteDialog = () => {
    if (campaignDeleting) return;
    setCampaignDeleteDialog({ open: false, name: null, type: null });
    setCampaignDeleteError(null);
  };

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;

  const group = data ?? {};
  const wabaCampaigns = group.wabaCampaigns ?? [];
  const rcsCampaigns = group.rcsCampaigns ?? [];
  const contactList = group.contactList ?? null;
  const baseFileId = group.baseFileId ?? null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  /* ── delete group handler ── */
  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const response = await axios.delete(
        `${API_ENDPOINT}/campaign/delete-campaign-group/${id}`,
        getHeader()
      );
      reloadDetail();
      toast.success(response.data.message);
    } catch (err) {
      setDeleteError(getErrorResponse(err));
      setDeleting(false);
    }
  };

  /* ── contact list submit handler ── */
  const handleContactListSubmit = async (selectedList) => {
    setContactListSubmitting(true);
    setContactListError(null);
    try {
      await axios.patch(
        `${API_ENDPOINT}/campaign/update-contact-list-to-group/${id}`,
        { contactList: selectedList },
        getHeader()
      );
      reloadDetail();
      setContactListOpen(false);
    } catch (err) {
      setContactListError(getErrorResponse(err));
    } finally {
      setContactListSubmitting(false);
    }
  };

  /* ── campaign delete handler ── */
  const handleCampaignDelete = async () => {
    const { name, type } = campaignDeleteDialog;
    const endpoint =
      type === "rcs"
        ? `${API_ENDPOINT}/campaign/delete-rcs-campaign-from-group/${id}/${encodeURIComponent(
            name
          )}`
        : `${API_ENDPOINT}/campaign/delete-waba-campaign-from-group/${id}/${encodeURIComponent(
            name
          )}`;

    setCampaignDeleting(true);
    setCampaignDeleteError(null);
    try {
      await axios.delete(endpoint, getHeader());
      reloadDetail();
      closeCampaignDeleteDialog();
    } catch (err) {
      setCampaignDeleteError(getErrorResponse(err));
    } finally {
      setCampaignDeleting(false);
    }
  };

  /* ── remove base file ID handler ── */
  const handleRemoveBaseFileId = async () => {
    setRemovingBaseFile(true);
    setRemoveBaseFileError(null);
    try {
      await axios.patch(
        `${API_ENDPOINT}/campaign/remove-base-file-id/${id}`,
        {},
        getHeader()
      );
      reloadDetail();
    } catch (err) {
      setRemoveBaseFileError(getErrorResponse(err));
    } finally {
      setRemovingBaseFile(false);
    }
  };

  const sections = [
    {
      title: "WhatsApp (WABA) Campaigns",
      items: wabaCampaigns,
      icon: FaWhatsapp,
      chipColor: "primary",
      type: "waba",
      nameKey: "campaignName",
      addRoute: `/home/campaign?gn=${data?.isDb ? 2 : 1}&name=${
        data?.contactList
      }&groupName=${data?.name}&contactsType=${
        data?.isDb ? "database" : "contacts"
      }`,
    },
    {
      title: "RCS Campaigns",
      items: rcsCampaigns,
      icon: MessagesSquare,
      chipColor: "secondary",
      type: "rcs",
      nameKey: "campaignName",
      addRoute: `/home/rcs2-campaign?name=${data?.contactList}&groupName=${
        data?.name
      }&contactsType=${data?.isDb ? "database" : "contacts"}`,
    },
  ];

  return (
    <Container maxWidth="md" sx={{ mt: 3, pb: 6 }}>
      <BackBtn />

      {/* ── Header row ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mt: 2,
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              bgcolor: isDb ? "warning.main" : "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: 2,
              flexShrink: 0,
            }}
          >
            {isDb ? (
              <Database size={24} strokeWidth={1.8} color="white" />
            ) : (
              <FolderOpen size={24} strokeWidth={1.8} color="white" />
            )}
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ letterSpacing: "-0.4px", lineHeight: 1.2 }}
              >
                {group.name ?? "—"}
              </Typography>
              {isDb && (
                <Chip
                  icon={<Database size={11} strokeWidth={2} />}
                  label="Database"
                  size="small"
                  color="warning"
                  sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
                />
              )}
            </Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.4 }}
            >
              <Calendar
                size={13}
                strokeWidth={1.8}
                style={{ color: "#9e9e9e" }}
              />
              <Typography variant="caption" color="text.disabled">
                Created {formatDate(group.createdOn)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined"
            endIcon={<BarChart2 strokeWidth={1.8} />}
            onClick={() => navigate(`/home/${id}/unified-reports`)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              px: 1.8,
              borderColor: "primary.light",
              color: "primary.dark",
              backgroundColor: "rgba(25, 118, 210, 0.06)",
              backdropFilter: "blur(8px)",
              "&:hover": {
                borderColor: "primary.main",
                color: "primary.main",
                backgroundColor: "rgba(25, 118, 210, 0.12)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Go to Reports
          </Button>

          <Button
            disabled={isLoading}
            variant="contained"
            startIcon={<Mic size={16} strokeWidth={2} />}
            onClick={() =>
              navigate(
                `/home/launch-voice-campaign?id=${id}&groupName=${data?.contactList}`
              )
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: 2,
              bgcolor: "success.main",
              "&:hover": { bgcolor: "success.dark" },
            }}
          >
            Launch Campaign
          </Button>

          <Tooltip title="Delete Group" arrow>
            <IconButton
              onClick={() => setDeleteOpen(true)}
              sx={{
                border: "1px solid",
                borderColor: "error.light",
                borderRadius: 1.5,
                color: "error.main",
                "&:hover": { bgcolor: "error.50" },
              }}
            >
              <Trash2 size={17} strokeWidth={1.8} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Stat cards ── */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <StatCard
          icon={FaWhatsapp}
          label="WhatsApp (WABA) Campaigns"
          count={wabaCampaigns.length}
          color="primary"
        />
        <StatCard
          icon={MessagesSquare}
          label="RCS Campaigns"
          count={rcsCampaigns.length}
          color="secondary"
        />
      </Box>

      {/* ── Contact List / Database Card ── */}
      <Box
        sx={{
          mb: 3,
          border: "1px solid",
          borderColor: contactList
            ? isDb
              ? "warning.300"
              : "primary.200"
            : "divider",
          borderRadius: 2.5,
          bgcolor: contactList
            ? isDb
              ? "warning.50"
              : "primary.50"
            : "background.paper",
          overflow: "hidden",
        }}
      >
        {/* Main contact list / database row */}
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: contactList
                  ? isDb
                    ? "warning.100"
                    : "primary.100"
                  : "grey.100",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {isDb ? (
                <Database
                  size={18}
                  strokeWidth={1.8}
                  style={{
                    color: contactList ? "#e65100" : "#999",
                  }}
                />
              ) : (
                <Users
                  size={18}
                  strokeWidth={1.8}
                  style={{ color: contactList ? "#1976d2" : "#999" }}
                />
              )}
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {isDb ? "Database" : "Contact List"}
                </Typography>
                {isDb && (
                  <Chip
                    label="DB"
                    size="small"
                    color="warning"
                    sx={{ height: 16, fontSize: 9, fontWeight: 700 }}
                  />
                )}
              </Box>
              {contactList ? (
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color={isDb ? "warning.dark" : "primary.main"}
                >
                  {contactList}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.disabled"
                  fontStyle="italic"
                >
                  {isDb
                    ? "No database associated"
                    : "No contact list associated"}
                </Typography>
              )}
            </Box>
          </Stack>

          <Button
            size="small"
            variant={contactList ? "outlined" : "contained"}
            color={isDb ? "warning" : "primary"}
            startIcon={
              contactList ? (
                <Pencil size={13} strokeWidth={2.5} />
              ) : (
                <Plus size={13} strokeWidth={2.5} />
              )
            }
            onClick={() => {
              setContactListError(null);
              setContactListOpen(true);
            }}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              py: 0.6,
              whiteSpace: "nowrap",
            }}
          >
            {contactList
              ? isDb
                ? "Change Database"
                : "Change Contact List"
              : isDb
              ? "Add Database"
              : "Add Contact List"}
          </Button>
        </Box>

        {/* ── Base File ID strip — visible only when present ── */}
        {baseFileId && (
          <>
            <Divider />
            <Box
              sx={{
                px: 2.5,
                py: 1.75,
                bgcolor: "warning.50",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              {/* Left: icon + info */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    mt: 0.2,
                    width: 30,
                    height: 30,
                    borderRadius: 1.5,
                    bgcolor: "warning.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <FolderOpen
                    size={14}
                    strokeWidth={2}
                    style={{ color: "#e65100" }}
                  />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="warning.dark"
                    display="block"
                    sx={{ lineHeight: 1.4 }}
                  >
                    Base File ID is attached to this list
                  </Typography>

                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      mt: 0.4,
                      px: 1,
                      py: 0.3,
                      bgcolor: "warning.100",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "warning.200",
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "monospace",
                        color: "warning.dark",
                        wordBreak: "break-all",
                        fontWeight: 600,
                        fontSize: 11,
                      }}
                    >
                      {baseFileId}
                    </Typography>
                  </Box>

                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: "block", mt: 0.6, fontStyle: "italic" }}
                  >
                    If you've added new contacts to this list, remove this ID so
                    the full list is used during the next campaign run.
                  </Typography>

                  {removeBaseFileError && (
                    <Alert
                      severity="error"
                      sx={{ mt: 1, borderRadius: 1.5, py: 0, fontSize: 12 }}
                    >
                      {removeBaseFileError}
                    </Alert>
                  )}
                </Box>
              </Box>

              {/* Right: remove button */}
              <Button
                size="small"
                variant="contained"
                disabled={removingBaseFile}
                startIcon={
                  removingBaseFile ? (
                    <CircularProgress size={13} color="inherit" />
                  ) : (
                    <X size={13} strokeWidth={2.5} />
                  )
                }
                onClick={handleRemoveBaseFileId}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 12,
                  py: 0.6,
                  px: 1.5,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  alignSelf: "flex-start",
                  bgcolor: "warning.main",
                  color: "warning.contrastText",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "warning.dark", boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "warning.200" },
                }}
              >
                {removingBaseFile ? "Removing…" : "Remove Base File ID"}
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* ── Campaign sections ── */}
      {sections.map(
        (
          { title, items, icon: Icon, chipColor, type, nameKey, addRoute },
          sectionIdx
        ) => (
          <Box key={title} sx={{ mb: 3 }}>
            {/* Section header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Icon size={16} strokeWidth={1.8} style={{ color: "#666" }} />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="text.secondary"
                >
                  {title}
                </Typography>
                <Chip
                  label={items.length}
                  size="small"
                  color={chipColor}
                  sx={{ height: 20, fontSize: 11 }}
                />
              </Box>

              <Button
                size="small"
                variant="outlined"
                color={chipColor}
                startIcon={<Plus size={14} strokeWidth={2.5} />}
                onClick={() => navigate(addRoute)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 12,
                  py: 0.5,
                }}
              >
                Add Campaign
              </Button>
            </Box>

            {/* Empty state */}
            {items.length === 0 ? (
              <Box
                sx={{
                  py: 3,
                  border: "1.5px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="text.disabled">
                  No {title.toLowerCase()} in this group
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {items.map((campaign, idx) => {
                  const campaignName =
                    campaign[nameKey] ?? `Campaign ${idx + 1}`;
                  return (
                    <Box
                      key={campaign._id ?? idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2.5,
                        py: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                        transition: "box-shadow 0.15s",
                        "&:hover": {
                          boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      {/* Left: name */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          minWidth: 0,
                        }}
                      >
                        <Zap
                          size={14}
                          strokeWidth={2}
                          style={{ color: "#bbb", flexShrink: 0 }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          title={campaignName}
                        >
                          {campaignName}
                        </Typography>
                      </Box>

                      {/* Right: date, status, view, delete */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexShrink: 0,
                        }}
                      >
                        {campaign.createdOn && (
                          <Typography variant="caption" color="text.disabled">
                            {formatDate(campaign.createdOn)}
                          </Typography>
                        )}
                        {campaign.status && (
                          <Chip
                            label={campaign.status}
                            size="small"
                            color={chipColor}
                            variant="outlined"
                            sx={{ height: 20, fontSize: 10 }}
                          />
                        )}

                        {/* View template button */}
                        {campaign.templateId && (
                          <Tooltip title="View Template" arrow>
                            <Button
                              size="small"
                              variant="outlined"
                              color={chipColor}
                              startIcon={<Eye size={13} strokeWidth={2} />}
                              onClick={() =>
                                openTemplateModal(campaign.templateId, type)
                              }
                              sx={{
                                borderRadius: 1.5,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: 11,
                                py: 0.3,
                                px: 1,
                                minWidth: 0,
                                whiteSpace: "nowrap",
                              }}
                            >
                              View
                            </Button>
                          </Tooltip>
                        )}

                        {/* Remove from group button */}
                        <Tooltip title="Remove from group" arrow>
                          <IconButton
                            size="small"
                            onClick={() =>
                              openCampaignDeleteDialog(campaignName, type)
                            }
                            sx={{
                              color: "error.main",
                              borderRadius: 1.5,
                              border: "1px solid transparent",
                              "&:hover": {
                                bgcolor: "error.50",
                                borderColor: "error.light",
                              },
                            }}
                          >
                            <Trash2 size={14} strokeWidth={2} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {sectionIdx < sections.length - 1 && <Divider sx={{ mt: 3 }} />}
          </Box>
        )
      )}

      {/* ── Message Report Tabs ── */}
      <CampaignReportTabs groupId={id} />

      {/* ── Template View Modal ── */}
      <TemplateViewModal
        open={templateModal.open}
        onClose={closeTemplateModal}
        templateId={templateModal.templateId}
        type={templateModal.type}
        cDetail={data}
      />

      {/* ── Contact List / Database Dialog ── */}
      <ContactListDialog
        open={contactListOpen}
        onClose={() => setContactListOpen(false)}
        currentList={contactList}
        listData={groupListData}
        listLoading={groupListLoading}
        listError={groupListError}
        isDb={isDb}
        onSubmit={handleContactListSubmit}
        submitting={contactListSubmitting}
        submitError={contactListError}
      />

      {/* ── Campaign Delete Dialog ── */}
      <CampaignDeleteDialog
        open={campaignDeleteDialog.open}
        onClose={closeCampaignDeleteDialog}
        campaign={campaignDeleteDialog}
        deleting={campaignDeleting}
        deleteError={campaignDeleteError}
        onConfirm={handleCampaignDelete}
      />

      {/* ── Delete Group Confirmation Dialog ── */}
      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Delete Campaign Group?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete <strong>{group.name}</strong> along
            with all its associations. This action cannot be undone.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={deleting}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleting}
            onClick={handleDelete}
            startIcon={
              deleting ? (
                <CircularProgress size={15} color="inherit" />
              ) : (
                <Trash2 size={15} />
              )
            }
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CampaignGroupDetail;
